import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import InputTextModal from "../../components/clientServiceSubscriptionTask/modalGeneralComponents/InputTextModal";
import { isValidLink } from "../../helpers/urlRegexValidator";
import { createChecklistItem } from "../../services/createChecklistItem";

const AddItemToJobModal = ({ open, setOpen, taskId, getNewChecklistItems }) => {
    const completeButtonRef = useRef(null);

    const [newChecklistItem, setNewChecklistItem] = useState({
        description: "",
        notes: "",
    });
    const [newLinkUrl, setNewLinkUrl] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const initializeForm = () => {
        setNewChecklistItem({ description: "", notes: "" });
        setNewLinkUrl("");
        setIsSaving(false);
    };

    const onHandleChangeDescription = (e) => {
        const { name, value } = e.target;
        setNewChecklistItem((prev) => ({ ...prev, [name]: value }));
    };

    const onHandleUrlChange = (e) => {
        setNewLinkUrl(e.target.value);
    };

    const onHandleSubmit = async (e) => {
        e.preventDefault();
        if (newChecklistItem.description.trim() === "") {
            toast.error("Description is required.");
            return;
        }

        // Sólo validamos la URL si el usuario ingresó algo
        let linksArray = [];
        if (newLinkUrl.trim()) {
            if (!isValidLink(newLinkUrl)) {
                toast.error("Please enter a valid URL.");
                return;
            }
            linksArray = [{ name: newLinkUrl, url: newLinkUrl }];
        }

        setIsSaving(true);
        try {
            const response = await createChecklistItem({
                task_id: taskId,
                description: newChecklistItem.description,
                links: JSON.stringify(linksArray),
            });
            if (response.data.success) {
                toast.success(response.data.message);
                await getNewChecklistItems();
                initializeForm();
                setOpen(false);
            } else {
                toast.error(response.data.message || "Error saving item.");
                setIsSaving(false);
            }
        } catch {
            toast.error("Network or server error.");
            setIsSaving(false);
        }
    };

    const onHandleCancel = () => {
        initializeForm();
        setOpen(false);
    };

    return (
        <AnimatePresence>
            {open && (
                <Dialog
                    initialFocus={completeButtonRef}
                    static
                    as={motion.div}
                    open={open}
                    onClose={onHandleCancel}
                    className="relative z-50"
                >
                    {/* Backdrop */}
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

                    {/* Full-screen panel */}
                    <Dialog.Panel className="fixed inset-0 p-6 overflow-y-auto bg-white">
                        <Dialog.Title className="mb-6 text-xl font-semibold text-center">
                            Add Checklist Item
                        </Dialog.Title>

                        <form onSubmit={onHandleSubmit} className="space-y-6">
                            {/* Description (seguirá siendo obligatorio) */}
                            <InputTextModal
                                required
                                label="Name"
                                name="description"
                                onChange={onHandleChangeDescription}
                                value={newChecklistItem.description}
                            />

                            {/* Link URL ahora opcional */}
                            <InputTextModal
                                required={false}
                                type="url"
                                label="Link URL (optional)"
                                name="url"
                                onChange={onHandleUrlChange}
                                value={newLinkUrl}
                            />

                            {/* Botones */}
                            <div className="flex space-x-4">
                                <button
                                    type="button"
                                    onClick={onHandleCancel}
                                    className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    ref={completeButtonRef}
                                    disabled={isSaving}
                                    className={`flex items-center justify-center flex-1 px-4 py-2 rounded-md text-white font-medium focus:outline-none ${
                                        isSaving
                                            ? "bg-teal-300 cursor-not-allowed"
                                            : "bg-teal-500 hover:bg-teal-600"
                                    }`}
                                >
                                    {isSaving && (
                                        <div className="w-5 h-5 mr-2 border-2 border-white rounded-full animate-spin border-t-transparent"></div>
                                    )}
                                    {isSaving ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </form>
                        {isSaving && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
                                <div className="w-12 h-12 border-4 border-teal-500 rounded-full border-t-transparent animate-spin"></div>
                            </div>
                        )}
                    </Dialog.Panel>
                </Dialog>
            )}
        </AnimatePresence>
    );
};

export default AddItemToJobModal;
